import { Component, OnInit, OnDestroy } from '@angular/core';
import { InrPatientProfile, InrRequest } from '../../../../shared/models/inr-patient-profile.model';
import { INRService } from '../../../../shared/services/inr.service';
import { ActivatedRoute } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Subject, takeUntil } from "rxjs";
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';

@Component({
  selector: 'app-inr-patient-profile',
  templateUrl: './inr-patient-profile.component.html',
  styleUrls: ['./inr-patient-profile.component.css'],
})
export class INRPatientProfileComponent implements OnInit, OnDestroy {
  patient: InrPatientProfile | null = null;
  selectedInrRequest: InrRequest | null = null;
  destroy$: Subject<any> = new Subject<any>();
  patientMsg: string = "";

  items: any;
  home: any;

  responseForm: FormGroup;
  minDate: Date = new Date(); // Minimum date is today (Issue 3)

  constructor(
    private inrService: INRService,
    private route: ActivatedRoute,
    public dialog: MatDialog,
    private fb: FormBuilder
  ) {
    this.responseForm = this.fb.group({
      nextMarivanDose: [null, [Validators.required, Validators.min(0), Validators.max(20)]],
      nextTestDate: [null, Validators.required],
      responseNote: ['', [Validators.maxLength(1000), this.alphanumericValidator]]
    });
  }

  ngOnInit(): void {
    this.loadPatientData();
  }

  loadPatientData() {
    const patientId = this.route.snapshot.paramMap.get('id');
    if (!patientId) return;

    this.inrService.getPatientById(patientId).pipe(takeUntil(this.destroy$)).subscribe((patient) => {
      this.patient = patient;
      if (this.patient?.inrRequest?.length > 0) {
        // Select the request with highest ID (most recent/pending) - matches sorted order
        const sortedRequests = [...this.patient.inrRequest].sort((a, b) => b.id - a.id);
        this.selectedInrRequest = sortedRequests[0];
      }
      this.items = [
        { label: this.patient.fullName }
      ];
      this.home = { icon: 'pi pi-home', routerLink: '/inr/patients' };
    });
  }

  submitResponse() {
    if (this.responseForm.valid && this.selectedInrRequest) {
      const formValue = this.responseForm.value;

      const payload = {
        nextMarivanDose: formValue.nextMarivanDose,
        nextTestDate: this.formatDateForApi(formValue.nextTestDate),
        responseNote: formValue.responseNote || ''
      };

      this.inrService.submitInrResponse(this.selectedInrRequest.id, payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.responseForm.reset();
            this.loadPatientData();
          },
          error: (err) => {
            console.error('Error submitting response', err);
          }
        });
    }
  }

  formatDateForApi(date: Date): string {
    if (!date) return '';
    const offsetDate = new Date(date.getTime() - (date.getTimezoneOffset() * 60000));
    return offsetDate.toISOString().split('T')[0];
  }

  onRequestSelected(request: InrRequest) {
    this.selectedInrRequest = request;
  }

  /**
   * Get the latest INR request (first in the array)
   */
  get latestInrRequest(): InrRequest | null {
    if (!this.patient?.inrRequest || this.patient.inrRequest.length === 0) {
      return null;
    }
    return this.patient.inrRequest[0];
  }

  /**
   * Calculate age from birth date
   */
  calculateAge(birthDate: string): number {
    if (!birthDate) return 0;
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  }

  /**
   * Format the nextTestDate array to a readable date string
   */
  formatNextTestDate(dateArray: number[] | undefined | null): Date | null {
    if (!dateArray || dateArray.length !== 3) return null;
    return new Date(dateArray[0], dateArray[1] - 1, dateArray[2]);
  }

  /**
   * Custom validator for alphanumeric characters and spaces only (Issue 6)
   * Rejects special characters and emoji
   */
  alphanumericValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null; // Allow empty (optional field)
    // Allow letters (including Arabic/Unicode), numbers, spaces, and basic punctuation
    const pattern = /^[\p{L}\p{N}\s.,!?\-():'"]+$/u;
    if (!pattern.test(control.value)) {
      return { invalidCharacters: true };
    }
    return null;
  }



  ngOnDestroy(): void {
    this.destroy$.next('');
    this.destroy$.complete();
  }
}
